<template>
  <v-dialog
    v-model="dialogDisponibilidad.estatus"
    hide-overlay
    persistent
    max-width="1600"
    scrollable
    content-class="dialog-disponibilidad-clases"
  >
    <v-card>
      <v-card-title class="text-subtitle-1 primary white--text">
        <v-icon left color="white">mdi-calendar-clock</v-icon>
        Panel de disponibilidad para cubrir clases
        <v-spacer></v-spacer>
        <v-btn icon dark @click="dialogDisponibilidad.estatus = false" small>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="max-height: 85vh; overflow-y: auto;">
        <!-- Tabs: Maestro (mi disponibilidad) vs Coordinador (ver todos) -->
        <v-tabs v-model="tabActiva" grow class="mb-4">
          <v-tab v-if="esMaestro">
            <v-icon left small>mdi-account-edit</v-icon>
            Mi disponibilidad
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-calendar-search</v-icon>
            Buscar suplentes
          </v-tab>
          <v-tab v-if="esCoordinador">
            <v-icon left small>mdi-view-grid</v-icon>
            Vista cuadrícula
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tabActiva">
          <!-- TAB 1: Mi disponibilidad (solo maestros) -->
          <v-tab-item v-if="esMaestro">
            <v-alert type="info" dense class="mb-3">
              Marca los horarios en los que estás disponible para cubrir clases. Los horarios donde ya tienes clase asignada aparecen en rojo y no pueden modificarse.
            </v-alert>

            <!-- Leyenda de colores -->
            <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
              <v-chip small><span class="color-box red"></span> Clase asignada (no editable)</v-chip>
              <v-chip small><span class="color-box green"></span> Disponible para cubrir</v-chip>
              <v-chip small><span class="color-box grey"></span> No disponible</v-chip>
              <v-chip small><span class="color-box yellow"></span> Con restricción</v-chip>
            </div>

            <!-- Cuadrícula de disponibilidad del maestro -->
            <v-simple-table dense class="elevation-1 mb-4">
              <thead>
                <tr>
                  <th class="text-left" style="min-width: 80px;">Hora</th>
                  <th v-for="dia in diasSemana" :key="dia.value" class="text-center">{{ dia.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bloque in bloquesHorario" :key="bloque.hora">
                  <td class="font-weight-medium">{{ bloque.label }}</td>
                  <td v-for="dia in diasSemanaKeys" :key="dia" class="pa-1">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="cell-disponibilidad pa-2 text-center"
                          :class="getCellClassMaestro(dia, bloque.hora)"
                          @click="toggleDisponibilidadMaestro(dia, bloque.hora)"
                        >
                          <v-icon v-if="getEstadoBloqueMaestro(dia, bloque.hora) === 'clase'" small>mdi-book</v-icon>
                          <v-icon v-else-if="getEstadoBloqueMaestro(dia, bloque.hora) === 'disponible'" small color="white">mdi-check</v-icon>
                          <v-icon v-else-if="getEstadoBloqueMaestro(dia, bloque.hora) === 'restriccion'" small color="white">mdi-alert</v-icon>
                          <span v-else>—</span>
                        </div>
                      </template>
                      <span>{{ getTooltipMaestro(dia, bloque.hora) }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- Observaciones -->
            <v-textarea
              v-model="observacionesMaestro"
              label="Observaciones (ej. Solo presencial, Solo online, Solo niveles básicos)"
              rows="2"
              filled
              dense
              hide-details
              class="mb-3"
            ></v-textarea>

            <v-btn color="primary" dark @click="guardarMiDisponibilidad" :loading="cargarGuardar">
              <v-icon left small>mdi-content-save</v-icon>
              Guardar disponibilidad
            </v-btn>
            <span v-if="ultimaActualizacionMaestro" class="ml-3 text-caption grey--text">
              Última actualización: {{ ultimaActualizacionMaestro }}
            </span>
          </v-tab-item>

          <!-- TAB 2: Buscar suplentes (filtros + lista) -->
          <v-tab-item>
            <v-alert type="info" dense class="mb-3">
              Filtra por día, horario, sucursal y nivel para encontrar maestros disponibles para cubrir clases.
            </v-alert>

            <v-row class="mb-4">
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="filtroDia"
                  :items="diasSemana"
                  label="Día"
                  dense
                  filled
                  hide-details
                  clearable
                  item-value="value"
                  item-text="label"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="filtroHora"
                  :items="bloquesHorarioFiltro"
                  label="Horario"
                  dense
                  filled
                  hide-details
                  clearable
                  item-value="hora"
                  item-text="label"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="filtroPlantel"
                  :items="planteles"
                  label="Sucursal"
                  dense
                  filled
                  hide-details
                  clearable
                  item-value="id_plantel"
                  item-text="plantel"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="filtroNivel"
                  :items="niveles"
                  label="Nivel"
                  dense
                  filled
                  hide-details
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="filtroModalidad"
                  :items="[{text: 'Online', value: 1}, {text: 'Presencial', value: 0}]"
                  label="Modalidad"
                  dense
                  filled
                  hide-details
                  clearable
                  item-value="value"
                  item-text="text"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-btn color="primary" @click="buscarDisponibles" block :loading="cargar">
                  <v-icon left small>mdi-magnify</v-icon>
                  Buscar
                </v-btn>
              </v-col>
            </v-row>

            <!-- Resultados -->
            <v-data-table
              :headers="headersResultados"
              :items="maestrosDisponiblesFiltrados"
              :loading="cargar"
              dense
              class="elevation-0"
              no-data-text="Selecciona filtros y haz clic en Buscar para ver maestros disponibles"
            >
              <template v-slot:item.telefono="{ item }">
                <span v-if="item.telefono">{{ item.telefono }}</span>
                <span v-else class="grey--text">—</span>
                <v-btn
                  v-if="item.telefono"
                  icon
                  x-small
                  class="ml-1"
                  @click="copiarTelefono(item.telefono)"
                >
                  <v-icon x-small>mdi-content-copy</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.telefono && esMovil"
                  icon
                  x-small
                  color="green"
                  :href="'tel:' + item.telefono"
                >
                  <v-icon x-small>mdi-phone</v-icon>
                </v-btn>
              </template>
              <template v-slot:item.disponible="{ item }">
                <v-chip x-small color="green" dark>{{ item.disponible }}</v-chip>
              </template>
            </v-data-table>

            <v-btn color="grey" small class="mt-3" @click="exportarExcel" :disabled="maestrosDisponiblesFiltrados.length === 0">
              <v-icon left small>mdi-microsoft-excel</v-icon>
              Exportar a Excel
            </v-btn>
          </v-tab-item>

          <!-- TAB 3: Vista cuadrícula (coordinadores) -->
          <v-tab-item v-if="esCoordinador">
            <v-alert type="info" dense class="mb-3">
              Vista general de la disponibilidad de todos los maestros. Haz clic en un maestro para ver su detalle.
            </v-alert>

            <v-row class="mb-3">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="searchMaestro"
                  label="Buscar maestro"
                  dense
                  filled
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filtroPlantelGrid"
                  :items="planteles"
                  label="Filtrar por sucursal"
                  dense
                  filled
                  hide-details
                  clearable
                  item-value="id_plantel"
                  item-text="plantel"
                ></v-autocomplete>
              </v-col>
            </v-row>

            <div class="grid-wrapper overflow-x-auto">
              <v-simple-table dense class="elevation-1" style="min-width: 900px;">
                <thead>
                  <tr>
                    <th class="text-left" style="min-width: 150px;">Maestro</th>
                    <th v-for="dia in diasSemana" :key="dia.value" class="text-center" style="min-width: 70px;">{{ dia.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="maestro in maestrosParaGrid" :key="maestro.id_usuario">
                    <td>
                      <div class="font-weight-medium">{{ maestro.nombre_completo }}</div>
                      <div class="text-caption grey--text">{{ maestro.plantel || '—' }}</div>
                      <div class="text-caption" v-if="maestro.telefono">
                        <v-btn x-small text @click="copiarTelefono(maestro.telefono)">
                          <v-icon x-small>mdi-phone</v-icon> {{ maestro.telefono }}
                        </v-btn>
                      </div>
                    </td>
                    <td v-for="dia in diasSemanaKeys" :key="dia" class="pa-1">
                      <div
                        class="cell-grid pa-1 text-caption"
                        @click="verDetalleDia(maestro, dia)"
                      >
                        <span v-for="(disp, i) in getDisponibilidadDia(maestro, dia)" :key="i" class="d-block">
                          <v-chip x-small :color="disp.color" dark>{{ disp.hora }}</v-chip>
                        </span>
                        <span v-if="!getDisponibilidadDia(maestro, dia).length" class="grey--text">—</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="dialogDisponibilidad.estatus = false" dark>
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog de carga -->
    <Alerta v-if="respuestaAlerta" :parametros="parametros"/>
    <carga v-if="cargar"/>
  </v-dialog>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'
import funcionesExcel from '@/mixins/funcionesExcel'
import { mapGetters } from 'vuex'

// Mapeo de bloques del sistema (lu1, ma1, etc.) a día
const BLOQUE_A_DIA = {
  lu1: 'lunes', lu2: 'lunes', lu3: 'lunes', lu4: 'lunes', lu5: 'lunes',
  ma1: 'martes', ma2: 'martes', ma3: 'martes', ma4: 'martes', ma5: 'martes',
  mi1: 'miercoles', mi2: 'miercoles', mi3: 'miercoles', mi4: 'miercoles', mi5: 'miercoles',
  ju1: 'jueves', ju2: 'jueves', ju3: 'jueves', ju4: 'jueves', ju5: 'jueves',
  vi1: 'viernes', vi2: 'viernes', vi3: 'viernes', vi4: 'viernes', vi5: 'viernes',
  sa1: 'sabado', sa2: 'sabado', sa3: 'sabado', sa4: 'sabado', sa5: 'sabado',
  do1: 'domingo', do2: 'domingo', do3: 'domingo', do4: 'domingo', do5: 'domingo'
}

export default {
  components: { Alerta, carga },
  mixins: [validarErrores, funcionesExcel],

  props: {
    dialogDisponibilidad: { type: Object, required: true },
    teachers: { type: Array, default: () => [] },
    gruposRoles: { type: Array, default: () => [] },
    planteles: { type: Array, default: () => [] },
    niveles: { type: Array, default: () => [] },
  },

  data: () => ({
    parametros: { dialogError: false, mensaje: '', color: '' },
    respuestaAlerta: false,
    cargar: false,
    cargarGuardar: false,
    tabActiva: 0,

    diasSemana: [
      { label: 'Lunes', value: 'lunes' },
      { label: 'Martes', value: 'martes' },
      { label: 'Miércoles', value: 'miercoles' },
      { label: 'Jueves', value: 'jueves' },
      { label: 'Viernes', value: 'viernes' },
      { label: 'Sábado', value: 'sabado' },
      { label: 'Domingo', value: 'domingo' },
    ],
    diasSemanaKeys: ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'],

    // Bloques de horario estándar (7:00 - 21:00, bloques de 1 hora)
    bloquesHorario: [
      { hora: '07:00', label: '7:00-8:00' },
      { hora: '08:00', label: '8:00-9:00' },
      { hora: '09:00', label: '9:00-10:00' },
      { hora: '10:00', label: '10:00-11:00' },
      { hora: '11:00', label: '11:00-12:00' },
      { hora: '12:00', label: '12:00-13:00' },
      { hora: '13:00', label: '13:00-14:00' },
      { hora: '14:00', label: '14:00-15:00' },
      { hora: '15:00', label: '15:00-16:00' },
      { hora: '16:00', label: '16:00-17:00' },
      { hora: '17:00', label: '17:00-18:00' },
      { hora: '18:00', label: '18:00-19:00' },
      { hora: '19:00', label: '19:00-20:00' },
      { hora: '20:00', label: '20:00-21:00' },
    ],

    // Estado de disponibilidad del maestro actual (mock local hasta conectar API)
    disponibilidadMaestro: {},
    observacionesMaestro: '',
    ultimaActualizacionMaestro: null,

    // Filtros búsqueda
    filtroDia: null,
    filtroHora: null,
    filtroPlantel: null,
    filtroNivel: null,
    filtroModalidad: null,
    filtroPlantelGrid: null,
    searchMaestro: '',

    // Resultados búsqueda - se llena con datos de API o mock
    maestrosDisponibles: [],

    headersResultados: [
      { text: 'Maestro', value: 'nombre_completo' },
      { text: 'Sucursal', value: 'plantel' },
      { text: 'Teléfono', value: 'telefono' },
      { text: 'Nivel', value: 'nivel' },
      { text: 'Disponible', value: 'disponible' },
      { text: 'Última actualización', value: 'fecha_actualizacion' },
    ],

    // IDs de puestos que son coordinadores (ajustar según sistema)
    idsCoordinador: [11, 18, 31, 12, 58, 43, 73, 25, 48, 49, 74, 62, 54, 70, 75],
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    bloquesHorarioFiltro() {
      return this.bloquesHorario.map(b => ({ hora: b.hora, label: b.label }))
    },

    esMaestro() {
      // Es maestro si está en teachers (tiene grupos asignados) o tiene rol académico
      const iderp = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp
      return this.teachers.some(t => (t.id_usuario || t.iderp) == iderp) || !!iderp
    },

    esCoordinador() {
      const datos = this.getdatosUsuarioERPERP
      const idpuesto = datos && datos.idpuesto
      return this.idsCoordinador.includes(idpuesto) || (datos && [1552, 1127, 1336, 1177, 886, 1593].includes(datos.iderp))
    },

    esMovil() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },

    maestrosDisponiblesFiltrados() {
      return this.maestrosDisponibles
    },

    maestrosParaGrid() {
      let list = this.teachers.filter(t => {
        const nombre = (t.nombre_completo || '').toLowerCase()
        const search = (this.searchMaestro || '').toLowerCase()
        if (search && !nombre.includes(search)) return false
        const idPlantelTeacher = this.getPlantelTeacher(t)
        if (this.filtroPlantelGrid && idPlantelTeacher != this.filtroPlantelGrid) return false
        return true
      })
      return list.slice(0, 50).map(t => ({
        ...t,
        plantel: this.getPlantelTeacherNombre(t),
      }))
    },
  },

  watch: {
    'dialogDisponibilidad.estatus'(val) {
      if (val) {
        this.inicializar()
      }
    },
  },

  methods: {
    inicializar() {
      this.tabActiva = this.esMaestro ? 0 : 1
      this.cargarMiDisponibilidad()
    },

    // Obtener clases asignadas del maestro actual desde gruposRoles
    getClasesAsignadasMaestro() {
      const iderp = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp
      if (!iderp) return []
      const clases = []
      const bloques = ['lu1','ma1','mi1','ju1','vi1','lu2','ma2','mi2','ju2','vi2','lu3','ma3','mi3','ju3','vi3','lu4','ma4','mi4','ju4','vi4','lu5','ma5','mi5','ju5','vi5','sa1','sa2','sa3','sa4','sa5','do1','do2','do3','do4','do5']
      for (const gr of this.gruposRoles) {
        const horaInicio = (gr.hora_inicio || '').toString().substr(0, 5)
        if (!horaInicio) continue
        for (const blq of bloques) {
          const idTeacher = gr[blq]
          if (idTeacher == iderp) {
            const dia = BLOQUE_A_DIA[blq]
            if (dia) clases.push({ dia, hora: horaInicio })
          }
        }
      }
      return clases
    },

    getEstadoBloqueMaestro(dia, hora) {
      const clases = this.getClasesAsignadasMaestro()
      const tieneClase = clases.some(c => c.dia === dia && c.hora === hora)
      if (tieneClase) return 'clase'

      const key = `${dia}_${hora}`
      const disp = this.disponibilidadMaestro[key]
      if (disp === true) return 'disponible'
      if (disp === 'restriccion') return 'restriccion'
      return 'no'
    },

    getCellClassMaestro(dia, hora) {
      const estado = this.getEstadoBloqueMaestro(dia, hora)
      return {
        'cell-clase': estado === 'clase',
        'cell-disponible': estado === 'disponible',
        'cell-restriccion': estado === 'restriccion',
        'cell-no': estado === 'no',
      }
    },

    getTooltipMaestro(dia, hora) {
      const estado = this.getEstadoBloqueMaestro(dia, hora)
      if (estado === 'clase') return 'Tienes clase asignada - No editable'
      if (estado === 'disponible') return 'Disponible para cubrir - Clic para quitar'
      if (estado === 'restriccion') return 'Disponible con restricción'
      return 'No disponible - Clic para marcar disponible'
    },

    toggleDisponibilidadMaestro(dia, hora) {
      const estado = this.getEstadoBloqueMaestro(dia, hora)
      if (estado === 'clase') return
      const key = `${dia}_${hora}`
      if (estado === 'disponible' || estado === 'restriccion') {
        this.$delete(this.disponibilidadMaestro, key)
      } else {
        this.$set(this.disponibilidadMaestro, key, this.observacionesMaestro ? 'restriccion' : true)
      }
    },

    cargarMiDisponibilidad() {
      const iderp = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp
      if (!iderp) {
        this.disponibilidadMaestro = {}
        return
      }
      this.cargar = true
      this.$http.get('disponibilidad.maestro/' + iderp).then(res => {
        this.disponibilidadMaestro = (res.data && res.data.disponibilidad) || {}
        this.observacionesMaestro = (res.data && res.data.observaciones) || ''
        this.ultimaActualizacionMaestro = (res.data && res.data.fecha_actualizacion) || null
      }).catch(() => {
        this.disponibilidadMaestro = {}
        this.observacionesMaestro = ''
        this.ultimaActualizacionMaestro = null
      }).finally(() => { this.cargar = false })
    },

    guardarMiDisponibilidad() {
      const iderp = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp
      if (!iderp) return this.validarErrorDirecto('No se identificó al usuario')
      this.cargarGuardar = true
      const payload = {
        id_teacher: iderp,
        disponibilidad: this.disponibilidadMaestro,
        observaciones: this.observacionesMaestro,
      }
      this.$http.post('disponibilidad.guardar', payload).then(() => {
        this.validarSuccess('Disponibilidad guardada correctamente')
        this.ultimaActualizacionMaestro = new Date().toLocaleString('es-MX')
        this.cargarMiDisponibilidad()
      }).catch(err => {
        this.validarError((err && err.response && err.response.data && err.response.data.message) || 'Error al guardar')
      }).finally(() => { this.cargarGuardar = false })
    },

    buscarDisponibles() {
      if (!this.filtroDia || !this.filtroHora) {
        return this.validarErrorDirecto('Selecciona al menos día y horario')
      }
      this.cargar = true
      const payload = {
        dia: this.filtroDia,
        hora: this.filtroHora,
        id_plantel: this.filtroPlantel,
        nivel: this.filtroNivel,
        modalidad: this.filtroModalidad,
      }
      this.$http.post('disponibilidad.buscar', payload).then(res => {
        this.maestrosDisponibles = res.data.maestros || []
      }).catch(() => {
        this.maestrosDisponibles = this.getMaestrosDisponiblesMock()
      }).finally(() => { this.cargar = false })
    },

    getPlantelTeacher(teacher) {
      const id = teacher.id_usuario || teacher.iderp
      const gr = this.gruposRoles.find(g => g.lu1 == id || g.ma1 == id || g.mi1 == id || g.ju1 == id || g.vi1 == id || g.lu2 == id || g.ma2 == id || g.mi2 == id || g.ju2 == id || g.vi2 == id)
      return gr && gr.id_plantel
    },
    getPlantelTeacherNombre(teacher) {
      const id = teacher.id_usuario || teacher.iderp
      const gr = this.gruposRoles.find(g => g.lu1 == id || g.ma1 == id || g.mi1 == id || g.ju1 == id || g.vi1 == id || g.lu2 == id || g.ma2 == id || g.mi2 == id || g.ju2 == id || g.vi2 == id)
      return (gr && gr.plantel) || '—'
    },
    getMaestrosDisponiblesMock() {
      const dia = this.filtroDia
      const hora = this.filtroHora
      const bloqueEncontrado = this.bloquesHorario.find(b => b.hora === hora)
      const label = (bloqueEncontrado && bloqueEncontrado.label) || hora
      return this.teachers.slice(0, 5).map(t => ({
        nombre_completo: t.nombre_completo,
        plantel: this.getPlantelTeacherNombre(t),
        telefono: t.telefono || t.celular || '—',
        nivel: t.nivel || '—',
        disponible: `${((this.diasSemana.find(d => d.value === dia) || {}).label) || dia} ${label}`,
        fecha_actualizacion: '—',
      }))
    },

    getDisponibilidadDia(maestro, dia) {
      const clases = this.getClasesMaestro(maestro)
      const disp = (maestro.disponibilidad || {})[dia] || []
      return [
        ...clases.filter(c => c.dia === dia).map(c => ({ hora: c.hora, color: 'red' })),
        ...(Array.isArray(disp) ? disp : []).map(h => ({ hora: h, color: 'green' })),
      ].slice(0, 3)
    },

    getClasesMaestro(maestro) {
      const idTeacher = maestro.id_usuario || maestro.iderp
      if (!idTeacher) return []
      const clases = []
      const bloques = ['lu1','ma1','mi1','ju1','vi1','lu2','ma2','mi2','ju2','vi2','lu3','ma3','mi3','ju3','vi3','lu4','ma4','mi4','ju4','vi4','lu5','ma5','mi5','ju5','vi5','sa1','sa2','sa3','sa4','sa5','do1','do2','do3','do4','do5']
      for (const gr of this.gruposRoles) {
        const horaInicio = (gr.hora_inicio || '').toString().substr(0, 5)
        if (!horaInicio) continue
        for (const blq of bloques) {
          if (gr[blq] == idTeacher) {
            const dia = BLOQUE_A_DIA[blq]
            if (dia) clases.push({ dia, hora: horaInicio })
          }
        }
      }
      return clases
    },

    verDetalleDia(maestro, dia) {
      // Podría abrir un mini-dialog con detalle
    },

    copiarTelefono(tel) {
      if (!tel) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(tel).then(() => {
          this.validarSuccess('Teléfono copiado')
        }).catch(() => {
          this.validarErrorDirecto('No se pudo copiar')
        })
      } else {
        this.validarErrorDirecto('No se pudo copiar')
      }
    },

    exportarExcel() {
      const datos = this.maestrosDisponiblesFiltrados.map(m => ({
        Maestro: m.nombre_completo,
        Sucursal: m.plantel,
        Teléfono: m.telefono,
        Nivel: m.nivel,
        Disponible: m.disponible,
        'Última actualización': m.fecha_actualizacion,
      }))
      this.exportarExcelDirecto(datos, 'disponibilidad_suplentes')
    },
  },
}
</script>

<style scoped>
.color-box {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}
.color-box.red { background: #f44336; }
.color-box.green { background: #4caf50; }
.color-box.grey { background: #9e9e9e; }
.color-box.yellow { background: #ffeb3b; }

.cell-disponibilidad {
  min-height: 36px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
.cell-disponibilidad.cell-clase {
  background: #ffcdd2 !important;
  cursor: not-allowed;
}
.cell-disponibilidad.cell-disponible {
  background: #4caf50 !important;
  color: white;
}
.cell-disponibilidad.cell-restriccion {
  background: #ffeb3b !important;
  color: #333;
}
.cell-disponibilidad.cell-no:hover {
  background: #e0e0e0;
}

.cell-grid {
  min-height: 40px;
  border: 1px solid #eee;
  border-radius: 4px;
  cursor: pointer;
}
</style>
